import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  EMPTY_PROGRESS,
  eventKindsToFetch,
  heroSlides,
  homeRows,
  resolveLocalizedText,
  upcomingEvents,
  type Channel,
  type Credentials,
  type HeroAction,
  type HomeAggregatorEntry,
  type Program,
  type SportEventView,
  type TextLocale,
  type VodItem,
  type WatchProgress,
} from '@player/domain';
import { PhoneHomeScreen, HomeScreen, useTranslator } from '@player/ui';
import type { Storefront } from '@player/ports';
import { usePhoneLayout } from './usePhoneLayout.js';
import type { Composition } from './composition.js';
import type { CatalogCache } from './catalogCache.js';

/**
 * Quel che serve alla Home: la vetrina dal gateway, il resto dall'apparecchio.
 *
 * ## Una chiamata sola, e non blocca niente
 *
 * `readStorefront` si chiama all'ingresso nella sezione. Se fallisce, la home resta senza fasce e lo
 * **dice**: non si inventa un ordine nostro, perché una home che assomiglia a quella configurata ma
 * non lo è manda l'assistenza a cercare un difetto nel pannello. Il predefinito, quando serve, lo
 * decide il backend — è lì che sta `DEFAULT_STOREFRONT_MODULES`, dove passa ogni pubblicazione.
 *
 * ## Il titolo si risolve qui
 *
 * La vetrina porta i titoli come mappa di lingue. Risolverli nel dominio vorrebbe dire passargli la
 * lingua corrente, che è una cosa dell'interfaccia; risolverli nella schermata vorrebbe dire farlo a
 * ogni disegno per ogni fascia. Qui è una volta sola, quando la vetrina arriva.
 */

export interface HomeHostProps {
  readonly composition: Composition;
  readonly cache: CatalogCache;
  readonly credentials: Credentials;
  readonly channels: ReadonlyArray<Channel>;
  /** I programmi già scaricati dalla guida, per canale. */
  readonly programsByChannel: ReadonlyMap<string, ReadonlyArray<Program>>;
  readonly categoryNames: ReadonlyMap<string, string>;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenVod: (item: VodItem) => void;
  readonly onOpenCategory: (categoryId: string) => void;
  /**
   * L'aggregatore scelto, **con etichetta e colore**.
   *
   * Il gateway restituisce il *contenuto* di un aggregatore, non come si chiama: il nome e il colore
   * stanno nella vetrina, che è qui. Passarli su evita che la schermata dell'aggregatore debba
   * richiedere la vetrina solo per scrivere un titolo.
   */
  readonly onOpenAggregator: (aggregatorId: string, label: string, color: string) => void;
  readonly onToggleReminder: (channelId: string, startMs: number) => void;
  /**
   * I promemoria già impostati.
   *
   * Servono all'hero per **riflettere lo stato**: senza, il tasto dice sempre «Imposta promemoria»
   * anche su un evento che ne ha già uno, e premendolo due volte lo si toglie credendo di metterlo.
   */
  readonly reminderIds: ReadonlySet<string>;
  /** Le animazioni dalle impostazioni: l'hero ruota solo se sono accese. */
  readonly animations: boolean;
  readonly onExit: () => void;
}

export function HomeHost({
  composition,
  cache,
  credentials,
  channels,
  programsByChannel,
  categoryNames,
  onOpenChannel,
  onOpenVod,
  onOpenCategory,
  onOpenAggregator,
  onToggleReminder,
  reminderIds,
  animations,
  onExit,
}: HomeHostProps): ReactNode {
  const translator = useTranslator();
  const phone = usePhoneLayout();
  const [storefront, setStorefront] = useState<Storefront | null>(null);
  const [vod, setVod] = useState<ReadonlyArray<VodItem>>([]);
  const [progress, setProgress] = useState<WatchProgress>(EMPTY_PROGRESS);
  const [recentIds, setRecentIds] = useState<ReadonlyArray<string>>([]);
  const [loading, setLoading] = useState(true);
  /** Gli eventi sportivi per tipologia, una chiamata per ogni `events` dichiarato dalla vetrina. */
  const [eventsByKind, setEventsByKind] = useState<ReadonlyMap<string, ReadonlyArray<SportEventView>>>(
    new Map(),
  );

  const gateway = useMemo(() => composition.createGateway(), [composition]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      gateway.readStorefront(credentials),
      cache.vod(credentials, 'movie', false),
      cache.vod(credentials, 'series', false),
      cache.progress(),
      cache.recentVod(),
    ])
      .then(([published, movies, series, stored, recent]) => {
        if (cancelled) return;
        setStorefront(published);
        setVod([...movies.items, ...series.items]);
        setProgress(stored);
        setRecentIds(recent);
      })
      .catch(() => {
        // La home resta senza fasce e lo dichiara. Vedi la nota in testa al modulo: un ordine
        // inventato qui sarebbe indistinguibile da quello dell'operatore, e quindi impossibile da
        // diagnosticare.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, cache, credentials]);

  const locale = translator.locale as TextLocale;

  const aggregators: ReadonlyArray<HomeAggregatorEntry> = useMemo(() => {
    if (storefront === null) return [];
    const out: HomeAggregatorEntry[] = [];
    for (const entry of storefront.aggregators) {
      const label = resolveLocalizedText(entry.label, locale);
      // `null` = nessuna lingua compilata. Sarebbe una piastrella muta: si salta invece di
      // disegnarla vuota o di scriverci l'identificativo.
      if (label === null) continue;
      out.push({ id: entry.id, label: label.text, color: entry.color, imageUrl: entry.imageUrl });
    }
    return out;
  }, [storefront, locale]);

  /*
    Gli eventi si chiedono **solo per le tipologie che servono a questa vetrina**, una per tipologia:
    quelle nominate dalle fasce, più tutte quelle accese se c'è un hero su un evento — che la sua
    tipologia non la dichiara. Quali siano lo decide `eventKindsToFetch`, nel dominio.

    Non a ogni disegno: `GET /api/v1/app/events/:kind` legge da Redis, ma resta una richiesta sullo
    stesso gateway che conta per indirizzo.

    Una tipologia che non risponde **non blocca le altre fasce**: si resta senza quella, e la fascia
    vuota non si disegna. È la stessa regola del catalogo che non arriva, e non è un ripiego
    silenzioso — non c'è niente da sostituire, e la home non promette un insieme fisso di fasce.
  */
  useEffect(() => {
    if (storefront === null) return undefined;
    // La decisione sta nel dominio, dove si prova senza montare niente: vedi `eventKindsToFetch`.
    const kinds = eventKindsToFetch(storefront);
    if (kinds.length === 0) return undefined;

    let cancelled = false;
    void Promise.all(
      kinds.map((kind) =>
        gateway
          .listEvents(credentials, kind)
          // `nowMs` è **l'istante del server**: è contro quello che si decide «in diretta», non
          // contro l'orologio dell'apparecchio, che su un televisore può essere sbagliato di ore.
          .then((result) => [kind, upcomingEvents(result.events, result.nowMs)] as const)
          .catch(() => [kind, [] as ReadonlyArray<SportEventView>] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setEventsByKind(new Map(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [storefront, gateway, credentials]);

  const modules = useMemo(() => {
    if (storefront === null) return [];
    return storefront.modules.map((module) => {
      const title = resolveLocalizedText(module.title, locale);
      return {
        type: module.type,
        // `null` = nessuna lingua compilata: si usa il nome predefinito della fascia, che sa solo
        // chi disegna.
        title: title === null ? null : title.text,
        params: module.params,
      };
    });
  }, [storefront, locale]);


  const hero = useMemo(
    () =>
      heroSlides({
        heroes: storefront?.heroes ?? [],
        vod,
        progress,
        reminders: reminderIds,
        eventsByKind,
        // I canali di questa linea: servono a **scegliere** quale aprire, non solo a scrivere un
        // nome. Vedi `playableChannelOf`.
        channels,
        /*
          L'istante del **server**, non `Date.now()`.

          Decide se un evento è oggi, domani o più in là, cioè se la riga dice «Stasera» o «Domani».
          L'orologio di un box da quaranta euro può essere sbagliato di ore, e in quel caso
          l'etichetta direbbe un giorno e il verdetto «in diretta» — che passa dall'istante del
          server — ne direbbe un altro: due orologi nella stessa schermata.

          `ServerClock` solleva se non è mai stato sincronizzato, e qui non può succedere: lo
          sincronizza l'accesso (`App.tsx`), che viene prima di questa schermata.
        */
        nowMs: composition.clock.nowMs(),
      }),
    [storefront, vod, progress, eventsByKind, channels, composition],
  );

  const rows = useMemo(
    () =>
      homeRows({
        modules,
        aggregators,
        vod,
        progress,
        recentIds,
        channels,
        programsByChannel,
        categoryNames,
        eventsByKind,
        nowMs: Date.now(),
      }),
    [
      modules,
      aggregators,
      vod,
      progress,
      recentIds,
      channels,
      programsByChannel,
      categoryNames,
      eventsByKind,
    ],
  );

  /*
    L'azione di una slide, dall'identificativo e da quale dei due tasti.

    La schermata del telefono dice **quale tasto** è stato premuto, non cosa fa: l'azione è un dato
    della slide, e farla comporre alla schermata vorrebbe dire due definizioni di «cosa succede
    premendo Guarda» — quella del televisore e quella del telefono.
  */
  const eseguiHero = (slideId: string, quale: 'primary' | 'secondary'): void => {
    const slide = hero.slides.find((candidata) => candidata.id === slideId);
    if (slide === undefined) return;
    const azione = quale === 'primary' ? slide.primary.action : slide.secondary?.action;
    if (azione === undefined) return;

    if (azione.kind === 'openVod') {
      const item = vod.find((entry) => entry.id === azione.itemId);
      if (item !== undefined) onOpenVod(item);
      return;
    }
    if (azione.kind === 'watchChannel') {
      onOpenChannel(azione.channelId);
      return;
    }
    onToggleReminder(azione.channelId, azione.startMs);
  };

  if (phone) {
    /*
      La home del telefono. Quali fasce entrano **non lo decide questa schermata**: lo dice
      `SHOWN_ON_PHONE` nel dominio, una tabella completa per tipo di fascia — aggiungendone una
      domani, il codice non compila finché qualcuno non dice se sul telefono si vede.
    */
    return (
      <PhoneHomeScreen
        heroSlides={hero.slides}
        rows={rows}
        onOpenHero={eseguiHero}
        /*
          Dove porta una casella dipende dal **tipo di fascia**, con la stessa corrispondenza della
          home del televisore (`HomeScreen`, lo `switch` su `row.kind`). Un elenco scritto due volte
          qui vorrebbe dire che la stessa casella porta in due posti diversi sui due apparecchi, e
          non ci sarebbe modo di accorgersene guardandone uno solo.
        */
        onOpenRowItem={(row, itemId) => {
          switch (row.kind) {
            case 'onair':
            case 'events':
              onOpenChannel(itemId);
              return;
            case 'cats':
              onOpenCategory(itemId);
              return;
            default: {
              const item = vod.find((entry) => entry.id === itemId);
              if (item !== undefined) onOpenVod(item);
            }
          }
        }}
      />
    );
  }

  return (
    <HomeScreen
      focusPort={composition.focusPort}
      heroSlides={hero.slides}
      animations={animations}
      onHeroAction={(action: HeroAction) => {
        if (action.kind === 'openVod') {
          const item = vod.find((entry) => entry.id === action.itemId);
          if (item !== undefined) onOpenVod(item);
          return;
        }
        if (action.kind === 'watchChannel') {
          onOpenChannel(action.channelId);
          return;
        }
        onToggleReminder(action.channelId, action.startMs);
      }}
      rows={rows}
      loading={loading}
      onOpenVod={onOpenVod}
      onOpenChannel={onOpenChannel}
      onOpenCategory={onOpenCategory}
      onOpenAggregator={(id) => {
        const entry = aggregators.find((candidate) => candidate.id === id);
        if (entry !== undefined) onOpenAggregator(entry.id, entry.label, entry.color);
      }}
      onExit={onExit}
    />
  );
}
